import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import type { IntegrationCallOutcomeDto, ModuleCode } from '@his/shared';
import { post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { describeIntegrationError, reportOutcome } from './external-outcome';

export interface SendCheckControls {
  send: () => Promise<boolean>;
  close: () => void;
  sending: boolean;
}

interface Props {
  path: string;
  label: string;
  doneTitle: string;
  module: Extract<ModuleCode, 'nphies' | 'wasfaty'>;
  permission: string;
  invalidate?: readonly (readonly unknown[])[];
  size?: 'sm' | 'default';
  variant?: 'outline' | 'default' | 'ghost';
  body?: unknown;
  disabled?: boolean;
  beforeSend?: (controls: SendCheckControls) => ReactNode;
}

export function ExternalSendButton({ path, label, doneTitle, module, permission, invalidate = [], size = 'sm', variant = 'outline', body, disabled, beforeSend }: Props) {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);

  const send = useMutation({
    mutationFn: () => post<IntegrationCallOutcomeDto>(path, body ?? {}),
    onSuccess: (outcome) => {
      reportOutcome(toast, outcome, doneTitle);
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: [...key] });
      setChecking(false);
    },
    onError: (error) => toast.error('Could not send', describeIntegrationError(error)),
  });

  if (!hasModule(module) || !can(permission)) return null;

  const controls: SendCheckControls = {
    send: () => send.mutateAsync().then(
      () => true,
      () => false,
    ),
    close: () => setChecking(false),
    sending: send.isPending,
  };

  return (
    <>
      <Button variant={variant} size={size} className="gap-1.5" loading={send.isPending} disabled={disabled} onClick={() => (beforeSend ? setChecking(true) : send.mutate())}>
        <Send className="h-3.5 w-3.5" /> {label}
      </Button>
      {checking && beforeSend?.(controls)}
    </>
  );
}
